function initHeroSlideshow(root = document) {
  root.querySelectorAll('[data-hero-slideshow]').forEach((hero) => {
    if (hero.dataset.heroInitialized === 'true') return;
    hero.dataset.heroInitialized = 'true';

    const slides = [...hero.querySelectorAll('[data-hero-slide]')];
    const dots = [...hero.querySelectorAll('[data-hero-dot]')];
    const autoplay = hero.dataset.autoplay === 'true';
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const imageDuration = Number(hero.dataset.autoplaySpeed) || 5000;
    let active = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
    let timer;
    let paused = false;

    const clearTimer = () => {
      window.clearTimeout(timer);
      timer = undefined;
    };

    const scheduleNext = () => {
      clearTimer();
      if (!autoplay || reducedMotion || paused || document.hidden || slides.length < 2) return;
      const video = slides[active]?.querySelector('video');
      if (video) return;
      timer = window.setTimeout(() => show(active + 1), imageDuration);
    };

    const show = (index) => {
      if (slides.length === 0) return;
      active = (index + slides.length) % slides.length;
      slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === active;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        const video = slide.querySelector('video');
        if (!video) return;
        video.loop = slides.length === 1;
        if (isActive && autoplay && !reducedMotion) {
          video.currentTime = 0;
          video.play().catch(() => scheduleNext());
        } else {
          video.pause();
        }
      });
      dots.forEach((dot, dotIndex) => {
        const isActive = dotIndex === active;
        dot.classList.toggle('is-active', isActive);
        if (isActive) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      scheduleNext();
    };

    slides.forEach((slide, slideIndex) => {
      slide.querySelector('video')?.addEventListener('ended', () => {
        if (slideIndex === active && autoplay && !paused) show(active + 1);
      });
    });
    dots.forEach((dot) => dot.addEventListener('click', () => show(Number(dot.dataset.heroDot))));
    hero.addEventListener('pointerenter', () => { paused = true; clearTimer(); });
    hero.addEventListener('pointerleave', () => { paused = false; scheduleNext(); });
    hero.addEventListener('focusin', () => { paused = true; clearTimer(); });
    hero.addEventListener('focusout', () => { paused = false; scheduleNext(); });
    document.addEventListener('visibilitychange', () => {
      const video = slides[active]?.querySelector('video');
      if (document.hidden) {
        clearTimer();
        video?.pause();
      } else if (video && autoplay && !reducedMotion) {
        video.play().catch(() => scheduleNext());
      } else {
        scheduleNext();
      }
    });

    show(active);
  });
}

function initCartDrawer() {
  const drawer = document.querySelector('[data-cart-drawer]');
  if (!drawer) return;

  const panel = drawer.querySelector('[data-cart-panel]');
  const loadingNode = drawer.querySelector('[data-cart-loading]');
  const emptyNode = drawer.querySelector('[data-cart-empty]');
  const itemsNode = drawer.querySelector('[data-cart-items]');
  const footerNode = drawer.querySelector('[data-cart-footer]');
  const errorNode = drawer.querySelector('[data-cart-error]');
  const shippingNode = drawer.querySelector('[data-cart-shipping]');
  const shippingMessageNode = drawer.querySelector('[data-cart-shipping-message]');
  const shippingValueNode = drawer.querySelector('[data-cart-shipping-value]');
  const progressNode = drawer.querySelector('[data-cart-progress]');
  const progressBarNode = drawer.querySelector('[data-cart-progress-bar]');
  const titleCountNode = drawer.querySelector('[data-cart-title-count]');
  const subtotalNode = drawer.querySelector('[data-cart-subtotal]');
  const savingsRowNode = drawer.querySelector('[data-cart-savings-row]');
  const savingsNode = drawer.querySelector('[data-cart-savings]');
  const protectionInput = drawer.querySelector('[data-cart-protection]');
  const noteInput = drawer.querySelector('[data-cart-note]');
  const recommendationsNode = drawer.querySelector('[data-cart-recommendations]');
  const recommendationListNode = drawer.querySelector('[data-cart-recommendation-list]');
  const discountsNode = drawer.querySelector('[data-cart-discounts]');
  const root = (drawer.dataset.cartRoot || '/').replace(/\/?$/, '/');
  const shippingThreshold = Number(drawer.dataset.freeShippingThreshold) || 0;
  const recommendationLimit = Number(drawer.dataset.recommendationLimit) || 3;
  const protectionVariantId = Number(drawer.dataset.protectionVariant) || 0;
  const recommendationsEnabled = drawer.dataset.recommendationsEnabled === 'true';
  const texts = {
    error: drawer.dataset.textError,
    shippingRemaining: drawer.dataset.textShippingRemaining,
    shippingUnlocked: drawer.dataset.textShippingUnlocked,
    add: drawer.dataset.textAdd,
    added: drawer.dataset.textAdded,
    remove: drawer.dataset.textRemove,
    increase: drawer.dataset.textIncrease,
    decrease: drawer.dataset.textDecrease,
    discountInvalid: drawer.dataset.textDiscountInvalid,
    discountApplied: drawer.dataset.textDiscountApplied,
    discountRemove: drawer.dataset.textDiscountRemove,
    noteSaved: drawer.dataset.textNoteSaved,
  };
  const state = { cart: null, busy: false, lastTrigger: null, recommendationRequest: 0, closeTimer: null };

  const route = (path) => `${root}${path.replace(/^\//, '')}`;
  const formatMoney = (cents, currency = state.cart?.currency || window.Shopify?.currency?.active || 'EUR') => (
    new Intl.NumberFormat(document.documentElement.lang || 'fr-FR', { style: 'currency', currency }).format((Number(cents) || 0) / 100)
  );

  const request = async (path, options = {}) => {
    const { headers = {}, ...requestOptions } = options;
    const response = await fetch(route(path), {
      credentials: 'same-origin',
      ...requestOptions,
      headers: { Accept: 'application/json', ...headers },
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.description || data.message || texts.error);
    return data;
  };

  const postJson = (path, body) => request(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body: JSON.stringify(body),
  });

  const isProtectionLine = (item) => (
    protectionVariantId > 0
    && Number(item.variant_id) === protectionVariantId
    && item.properties?._cart_drawer_addon === 'delivery_protection'
  );

  const merchandiseItems = (cart) => cart.items.filter((item) => !isProtectionLine(item));
  const protectionLine = (cart) => cart.items.find(isProtectionLine);

  const showMessage = (message = '', success = false) => {
    if (!errorNode) return;
    errorNode.textContent = message;
    errorNode.hidden = !message;
    errorNode.classList.toggle('is-success', success);
  };

  const setBusy = (busy) => {
    state.busy = busy;
    drawer.classList.toggle('is-busy', busy);
    panel?.setAttribute('aria-busy', String(busy));
    drawer.querySelectorAll('button:not([data-cart-close]), input, textarea').forEach((control) => {
      control.disabled = busy;
    });
  };

  const updateCartCount = (count) => {
    document.querySelectorAll('[data-cart-count]').forEach((node) => { node.textContent = count; });
    if (titleCountNode) titleCountNode.textContent = count > 0 ? `· ${count}` : '';
  };

  const createQuantityButton = (item, quantity, action, label, symbol) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.dataset.cartLineAction = action;
    button.dataset.lineKey = item.key;
    button.dataset.quantity = String(quantity);
    button.setAttribute('aria-label', `${label} ${item.product_title}`);
    button.textContent = symbol;
    return button;
  };

  const renderItem = (item) => {
    const article = document.createElement('article');
    article.className = 'conversion-cart__item';

    const link = document.createElement('a');
    link.className = 'conversion-cart__item-image';
    link.href = item.url;
    if (item.image) {
      const image = document.createElement('img');
      image.src = item.image;
      image.alt = item.product_title;
      image.loading = 'lazy';
      link.append(image);
    }

    const details = document.createElement('div');
    details.className = 'conversion-cart__item-details';
    const title = document.createElement('a');
    title.href = item.url;
    title.className = 'conversion-cart__item-title';
    title.textContent = item.product_title;
    details.append(title);
    if (item.variant_title && item.variant_title !== 'Default Title') {
      const variant = document.createElement('span');
      variant.className = 'conversion-cart__item-variant';
      variant.textContent = item.variant_title;
      details.append(variant);
    }

    const controls = document.createElement('div');
    controls.className = 'conversion-cart__item-controls';
    const quantity = document.createElement('div');
    quantity.className = 'conversion-cart__quantity';
    quantity.append(
      createQuantityButton(item, item.quantity - 1, 'change', texts.decrease, '−'),
      Object.assign(document.createElement('span'), { textContent: String(item.quantity) }),
      createQuantityButton(item, item.quantity + 1, 'change', texts.increase, '+'),
    );
    const remove = createQuantityButton(item, 0, 'remove', texts.remove, texts.remove);
    remove.className = 'conversion-cart__remove';
    controls.append(quantity, remove);
    details.append(controls);

    const price = document.createElement('div');
    price.className = 'conversion-cart__item-price';
    price.textContent = formatMoney(item.final_line_price);
    if (item.original_line_price > item.final_line_price) {
      const original = document.createElement('s');
      original.textContent = formatMoney(item.original_line_price);
      price.prepend(original);
    }

    article.append(link, details, price);
    return article;
  };

  const renderDiscounts = (cart) => {
    if (!discountsNode) return;
    discountsNode.replaceChildren();
    (cart.discount_codes || []).filter((discount) => discount.applicable).forEach((discount) => {
      const chip = document.createElement('span');
      chip.textContent = discount.code;
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.dataset.cartDiscountRemove = discount.code;
      remove.setAttribute('aria-label', `${texts.discountRemove} ${discount.code}`);
      remove.textContent = '×';
      chip.append(remove);
      discountsNode.append(chip);
    });
  };

  const renderShipping = (items) => {
    const merchandiseSubtotal = items.reduce((sum, item) => sum + item.final_line_price, 0);
    const visible = shippingThreshold > 0 && items.length > 0;
    shippingNode.hidden = !visible;
    if (!visible) return;
    const remaining = Math.max(0, shippingThreshold - merchandiseSubtotal);
    const percentage = Math.min(100, Math.round((merchandiseSubtotal / shippingThreshold) * 100));
    shippingMessageNode.textContent = remaining > 0 ? texts.shippingRemaining.replace('[amount]', formatMoney(remaining)) : texts.shippingUnlocked;
    shippingValueNode.textContent = `${percentage}%`;
    progressNode.setAttribute('aria-valuenow', String(percentage));
    progressBarNode.style.width = `${percentage}%`;
  };

  const recommendationImage = (product) => {
    const image = product.featured_image || product.images?.[0];
    return typeof image === 'string' ? image : image?.src;
  };

  const renderRecommendations = (products) => {
    recommendationListNode.replaceChildren();
    products.forEach((product) => {
      const variant = product.variants?.find((entry) => entry.available);
      if (!variant) return;
      const hasOptions = (product.variants?.length || 0) > 1;
      const article = document.createElement('article');
      article.className = 'conversion-cart__recommendation';
      const link = document.createElement('a');
      link.href = product.url;
      link.className = 'conversion-cart__recommendation-image';
      const imageUrl = recommendationImage(product);
      if (imageUrl) {
        const image = document.createElement('img');
        image.src = imageUrl;
        image.alt = product.title;
        image.loading = 'lazy';
        link.append(image);
      }
      const copy = document.createElement('div');
      const title = document.createElement('a');
      title.href = product.url;
      title.textContent = product.title;
      const price = document.createElement('span');
      price.textContent = hasOptions
        ? `Dès ${formatMoney(product.price_min ?? product.price ?? variant.price)}`
        : formatMoney(variant.price ?? product.price);
      copy.append(title, price);
      let action;
      if (hasOptions) {
        // Produit avec options (texture, taille, longueur) : on envoie vers la fiche produit
        action = document.createElement('a');
        action.href = product.url;
        action.className = 'button button--outline';
        action.setAttribute('aria-label', `Choisir les options de ${product.title}`);
        action.textContent = 'Choisir';
      } else {
        // Produit sans options : ajout direct au panier
        action = document.createElement('button');
        action.type = 'button';
        action.className = 'button button--outline';
        action.dataset.cartRecommendationVariant = variant.id;
        action.setAttribute('aria-label', `${texts.add} ${product.title}`);
        action.textContent = texts.add;
      }
      article.append(link, copy, action);
      recommendationListNode.append(article);
    });
    recommendationsNode.hidden = recommendationListNode.children.length === 0;
  };

  const loadRecommendations = async (items) => {
    recommendationsNode.hidden = true;
    recommendationListNode.replaceChildren();
    if (!recommendationsEnabled || items.length === 0) return;
    const requestId = ++state.recommendationRequest;
    const productIds = new Set(items.map((item) => Number(item.product_id)));
    const sourceId = items[0].product_id;
    try {
      let response = await request(`recommendations/products.json?product_id=${encodeURIComponent(sourceId)}&limit=${recommendationLimit}&intent=complementary`);
      let products = (response.products || []).filter((product) => !productIds.has(Number(product.id)));
      if (products.length === 0) {
        response = await request(`recommendations/products.json?product_id=${encodeURIComponent(sourceId)}&limit=${recommendationLimit}&intent=related`);
        products = (response.products || []).filter((product) => !productIds.has(Number(product.id)));
      }
      if (requestId === state.recommendationRequest) renderRecommendations(products.slice(0, recommendationLimit));
    } catch {
      if (requestId === state.recommendationRequest) recommendationsNode.hidden = true;
    }
  };
  const discountPercent = (cart) => {
    const applications = [...(cart.cart_level_discount_applications || [])];
    (cart.items || []).forEach((item) => {
      (item.line_level_discount_allocations || []).forEach((allocation) => {
        if (allocation.discount_application) applications.push(allocation.discount_application);
      });
    });
    const percentage = applications.find((app) => app.value_type === 'percentage');
    if (percentage) return Math.round(Number(percentage.value));
    const original = Number(cart.original_total_price) || 0;
    const savings = Number(cart.total_discount) || 0;
    return original > 0 ? Math.round((savings / original) * 100) : 0;
  };
  const renderCart = (cart) => {
    state.cart = cart;
    const items = merchandiseItems(cart);
    const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
    loadingNode.hidden = true;
    emptyNode.hidden = items.length > 0;
    itemsNode.hidden = items.length === 0;
    footerNode.hidden = items.length === 0;
    itemsNode.replaceChildren(...items.map(renderItem));
    updateCartCount(itemCount);
    renderShipping(items);
    if (subtotalNode) subtotalNode.textContent = formatMoney(cart.total_price, cart.currency);
    const savings = Number(cart.total_discount) || 0;
    if (savingsRowNode) savingsRowNode.hidden = savings <= 0;
    if (savingsNode) savingsNode.textContent = `−${formatMoney(savings, cart.currency)}`;
    // --- Message 🎁 réduction appliquée ---
    let giftNode = document.querySelector('[data-cart-gift-banner]');
    if (!giftNode && savingsRowNode) {
      giftNode = document.createElement('div');
      giftNode.setAttribute('data-cart-gift-banner', '');
      giftNode.style.cssText = 'margin:0 0 10px;padding:10px 12px;background:#f6efe4;border:1px solid #e3d3b8;color:#7a5a2b;font-size:13px;text-align:center;letter-spacing:.02em;';
      savingsRowNode.before(giftNode);
    }
    if (giftNode) {
      const percent = discountPercent(cart);
      giftNode.style.display = savings > 0 ? 'block' : 'none';
      giftNode.textContent = percent > 0
        ? `🎁 -${percent} % appliqués à votre commande`
        : `🎁 −${formatMoney(savings, cart.currency)} appliqués à votre commande`;
    }
    // --- fin du message ---
    if (protectionInput) protectionInput.checked = Boolean(protectionLine(cart));
    if (noteInput && document.activeElement !== noteInput) noteInput.value = cart.note || '';
    renderDiscounts(cart);
    loadRecommendations(items);
  };

  const removeOrphanProtection = async (cart) => {
    const protection = protectionLine(cart);
    if (!protection || merchandiseItems(cart).length > 0) return cart;
    return postJson('cart/change.js', { id: protection.key, quantity: 0 });
  };

  const refreshCart = async () => {
    const cart = await request('cart.js');
    renderCart(await removeOrphanProtection(cart));
  };

  const showDrawer = (trigger) => {
    window.clearTimeout(state.closeTimer);
    state.lastTrigger = trigger || document.activeElement;
    drawer.hidden = false;
    drawer.setAttribute('aria-hidden', 'false');
    document.body.dataset.cartDrawerOpen = '';
    window.requestAnimationFrame(() => {
      drawer.classList.add('is-open');
      panel?.focus();
    });
  };

  const openDrawer = async (trigger) => {
    showDrawer(trigger);
    loadingNode.hidden = false;
    emptyNode.hidden = true;
    itemsNode.hidden = true;
    footerNode.hidden = true;
    showMessage();
    try {
      await refreshCart();
    } catch (error) {
      loadingNode.hidden = true;
      showMessage(error.message || texts.error);
    }
  };

  const closeDrawer = () => {
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    delete document.body.dataset.cartDrawerOpen;
    state.closeTimer = window.setTimeout(() => { drawer.hidden = true; }, 350);
    state.lastTrigger?.focus?.();
  };

  const mutateCart = async (path, body) => {
    if (state.busy) return;
    setBusy(true);
    showMessage();
    try {
      const response = await postJson(path, body);
      const cart = path.endsWith('cart/add.js') ? await request('cart.js') : response;
      renderCart(await removeOrphanProtection(cart));
    } catch (error) {
      showMessage(error.message || texts.error);
    } finally {
      setBusy(false);
    }
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-cart-open]');
    if (!trigger || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    openDrawer(trigger);
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('.product-form');
    if (!form || event.defaultPrevented || event.submitter?.closest('.shopify-payment-button')) return;
    event.preventDefault();
    const submit = event.submitter || form.querySelector('[type="submit"]');
    showDrawer(submit);
    loadingNode.hidden = false;
    setBusy(true);
    showMessage();
    try {
      await request('cart/add.js', {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        body: new FormData(form),
      });
      await refreshCart();
      showMessage(texts.added, true);
    } catch (error) {
      loadingNode.hidden = true;
      showMessage(error.message || texts.error);
    } finally {
      setBusy(false);
    }
  });

  drawer.addEventListener('click', (event) => {
    const close = event.target.closest('[data-cart-close]');
    if (close) {
      if (close.tagName === 'BUTTON') event.preventDefault();
      closeDrawer();
      return;
    }
    const action = event.target.closest('[data-cart-line-action]');
    if (action) {
      mutateCart('cart/change.js', { id: action.dataset.lineKey, quantity: Number(action.dataset.quantity) });
      return;
    }
    const recommendation = event.target.closest('[data-cart-recommendation-variant]');
    if (recommendation) {
      mutateCart('cart/add.js', { items: [{ id: Number(recommendation.dataset.cartRecommendationVariant), quantity: 1 }] });
      return;
    }
    const discountRemove = event.target.closest('[data-cart-discount-remove]');
    if (discountRemove) {
      const codes = (state.cart?.discount_codes || [])
        .filter((discount) => discount.applicable && discount.code !== discountRemove.dataset.cartDiscountRemove)
        .map((discount) => discount.code);
      mutateCart('cart/update.js', { discount: codes.join(',') });
    }
  });

  protectionInput?.addEventListener('change', async () => {
    if (protectionInput.checked) {
      if (merchandiseItems(state.cart || { items: [] }).length === 0) {
        protectionInput.checked = false;
        return;
      }
      await mutateCart('cart/add.js', {
        items: [{ id: protectionVariantId, quantity: 1, properties: { _cart_drawer_addon: 'delivery_protection' } }],
      });
      return;
    }
    const line = protectionLine(state.cart || { items: [] });
    if (line) await mutateCart('cart/change.js', { id: line.key, quantity: 0 });
  });

  drawer.querySelector('[data-cart-discount-form]')?.addEventListener('submit', async (event) => {
    event.preventDefault();
    const input = drawer.querySelector('[data-cart-discount-input]');
    const code = input.value.trim();
    if (!code || state.busy) return;
    const existing = (state.cart?.discount_codes || []).filter((discount) => discount.applicable).map((discount) => discount.code);
    setBusy(true);
    showMessage();
    try {
      const cart = await postJson('cart/update.js', { discount: [...new Set([...existing, code])].join(',') });
      renderCart(cart);
      const applied = (cart.discount_codes || []).some((discount) => discount.applicable && discount.code.toLowerCase() === code.toLowerCase());
      showMessage(applied ? texts.discountApplied : texts.discountInvalid, applied);
      if (applied) input.value = '';
    } catch (error) {
      showMessage(error.message || texts.discountInvalid);
    } finally {
      setBusy(false);
    }
  });

  drawer.querySelector('[data-cart-note-form]')?.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (state.busy) return;
    setBusy(true);
    showMessage();
    try {
      const cart = await postJson('cart/update.js', { note: noteInput?.value || '' });
      renderCart(cart);
      showMessage(texts.noteSaved, true);
    } catch (error) {
      showMessage(error.message || texts.error);
    } finally {
      setBusy(false);
    }
  });

  drawer.addEventListener('rqh:flash-discount-updated', (event) => {
    if (!event.detail?.cart) return;
    renderCart(event.detail.cart);
    showMessage(texts.discountApplied, true);
  });

  drawer.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      closeDrawer();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = [...panel.querySelectorAll('a[href], button:not(:disabled), input:not(:disabled), textarea:not(:disabled), summary, [tabindex]:not([tabindex="-1"])')]
      .filter((element) => !element.closest('[hidden]'));
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  window.RQHCart = { open: openDrawer, close: closeDrawer, refresh: refreshCart };
}

function initTextureDashes(root = document) {
  root.querySelectorAll('.texture-carousel-wrap').forEach((wrap) => {
    if (wrap.dataset.textureDashesInitialized === 'true') return;
    const slider = wrap.querySelector('.featured-texture-slider');
    const dashes = wrap.querySelector('[data-texture-dashes]');
    if (!slider || !dashes) return;
    const buttons = [...dashes.querySelectorAll('[data-texture-index]')];
    if (buttons.length < 2) return;
    wrap.dataset.textureDashesInitialized = 'true';

    const slideDistance = () => {
      const slide = slider.querySelector('.texture-slide');
      const gap = Number.parseFloat(window.getComputedStyle(slider).columnGap) || 0;
      return (slide?.getBoundingClientRect().width || slider.clientWidth) + gap;
    };
    const setActive = (index) => {
      buttons.forEach((button, buttonIndex) => {
        button.classList.toggle('is-active', buttonIndex === index);
        if (buttonIndex === index) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      });
    };

    slider.addEventListener('scroll', () => {
      setActive(Math.min(buttons.length - 1, Math.round(slider.scrollLeft / Math.max(1, slideDistance()))));
    }, { passive: true });
    buttons.forEach((button, index) => {
      button.addEventListener('click', () => {
        slider.scrollTo({ left: index * slideDistance(), behavior: 'smooth' });
      });
    });
  });
}

function initFlashCountdown(root = document) {
  root.querySelectorAll('[data-flash-countdown]').forEach((banner) => {
    if (banner.dataset.flashInitialized === 'true') return;
    banner.dataset.flashInitialized = 'true';

    const timerNode = banner.querySelector('[data-flash-timer]');
    const activeNode = banner.querySelector('[data-flash-active]');
    const expiredNode = banner.querySelector('[data-flash-expired]');
    const claimNode = banner.querySelector('[data-flash-claim]');
    const statusNode = banner.querySelector('[data-flash-status]');
    const duration = Math.max(60, Number(banner.dataset.flashDuration) || 900);
    const code = (banner.dataset.flashCode || 'FLASH10').trim();
    const cartRoot = (banner.dataset.cartRoot || '/').replace(/\/?$/, '/');
    const storageKey = `rqh-cart-flash-expiry:${code.toLowerCase()}:${duration}`;
    const drawer = banner.closest('[data-cart-drawer]');
    let expiry = 0;
    let interval;
    const expire = () => {
      window.clearInterval(interval);
      if (activeNode) activeNode.hidden = true;
      if (expiredNode) expiredNode.hidden = false;
    };
    const render = () => {
      const remaining = Math.max(0, Math.ceil((expiry - Date.now()) / 1000));
      if (remaining <= 0) {
        expire();
        return;
      }
      const minutes = Math.floor(remaining / 60);
      const seconds = remaining % 60;
      if (timerNode) timerNode.textContent = `${minutes}:${String(seconds).padStart(2, '0')}`;
    };
    const start = () => {
      if (banner.dataset.flashStarted === 'true') return;
      banner.dataset.flashStarted = 'true';
      try {
        expiry = Number(window.localStorage.getItem(storageKey));
      } catch {
        expiry = 0;
      }
      if (!Number.isFinite(expiry) || expiry <= 0) {
        expiry = Date.now() + duration * 1000;
        try {
          window.localStorage.setItem(storageKey, String(expiry));
        } catch {
          // Storage can be unavailable in strict privacy modes; the timer still works for this page view.
        }
      }
      render();
      if (expiry > Date.now()) interval = window.setInterval(render, 1000);
    };

    if (!drawer || !drawer.hidden) {
      start();
    } else {
      const observer = new MutationObserver(() => {
        if (drawer.hidden) return;
        observer.disconnect();
        start();
      });
      observer.observe(drawer, { attributes: true, attributeFilter: ['hidden'] });
    }

    claimNode?.addEventListener('click', async () => {
      if (!code || claimNode.disabled) return;
      const defaultLabel = claimNode.dataset.labelDefault || claimNode.textContent;
      claimNode.disabled = true;
      claimNode.textContent = 'Activation…';
      if (statusNode) statusNode.hidden = true;
      try {
        const cartResponse = await fetch(`${cartRoot}cart.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
        if (!cartResponse.ok) throw new Error('Cart unavailable');
        const currentCart = await cartResponse.json();
        const existingCodes = (currentCart.discount_codes || [])
          .filter((discount) => discount.applicable)
          .map((discount) => discount.code);
        const updateResponse = await fetch(`${cartRoot}cart/update.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ discount: [...new Set([...existingCodes, code])].join(',') }),
        });
        if (!updateResponse.ok) throw new Error('Discount unavailable');
        const cart = await updateResponse.json();
        const applied = (cart.discount_codes || []).some((discount) => (
          discount.applicable && discount.code.toLowerCase() === code.toLowerCase()
        ));
        if (!applied) throw new Error('Discount invalid');
        claimNode.textContent = claimNode.dataset.labelApplied || 'Remise activée';
        if (statusNode) {
          statusNode.textContent = `Le code ${code} est appliqué au total du panier.`;
          statusNode.hidden = false;
        }
        banner.dispatchEvent(new CustomEvent('rqh:flash-discount-updated', { bubbles: true, detail: { cart } }));
        if (!drawer) window.location.reload();
      } catch {
        claimNode.disabled = false;
        claimNode.textContent = defaultLabel;
        if (statusNode) {
          statusNode.textContent = `Le code ${code} n’a pas pu être appliqué. Vérifiez qu’il est actif.`;
          statusNode.hidden = false;
        }
      }
    });
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initHeroSlideshow();
  initTextureDashes();
  initFlashCountdown();
  initCartDrawer();
  const drawer = document.querySelector('[data-mobile-menu]');
  document.querySelector('[data-menu-toggle]')?.addEventListener('click', () => { drawer.hidden = false; });
  document.querySelector('[data-menu-close]')?.addEventListener('click', () => { drawer.hidden = true; });

  document.querySelectorAll('[data-product-section]').forEach((section) => {
    const forceUnavailable = section.dataset.forceUnavailable === 'true';
    const mediaButtons = [...section.querySelectorAll('[data-media-target]')];
    mediaButtons.forEach((button, index) => {
      button.classList.toggle('is-active', index === 0);
      if (index === 0) button.setAttribute('aria-current', 'true');
      button.addEventListener('click', () => {
        section.querySelectorAll('[data-media-id]').forEach((media) => { media.hidden = media.dataset.mediaId !== button.dataset.mediaTarget; });
        mediaButtons.forEach((candidate) => {
          const active = candidate === button;
          candidate.classList.toggle('is-active', active);
          if (active) candidate.setAttribute('aria-current', 'true');
          else candidate.removeAttribute('aria-current');
        });
      });
    });
    const form = section.querySelector('.product-form');
    const variantsNode = section.querySelector('[data-product-json]');
    if (!form || !variantsNode) return;
    const variants = JSON.parse(variantsNode.textContent);
    form.addEventListener('change', () => {
      const selected = [...form.querySelectorAll('fieldset')].map((fieldset) => fieldset.querySelector('input:checked')?.value);
      const variant = variants.find((entry) => entry.options.every((option, index) => option === selected[index]));
      if (!variant) return;
      form.querySelector('[data-variant-id]').value = variant.id;
      const submit = form.querySelector('.product-submit');
      submit.disabled = forceUnavailable || !variant.available;
      const currency = window.Shopify?.currency?.active || 'EUR';
      const formatter = new Intl.NumberFormat(document.documentElement.lang || 'fr-FR', { style: 'currency', currency });
      const price = section.querySelector('[data-product-price]');
      const klarnaAmount = section.querySelector('[data-klarna-amount]');
      if (price) price.textContent = formatter.format(variant.price / 100);
      if (klarnaAmount) klarnaAmount.textContent = formatter.format(variant.price / 300);
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    });
  });

  document.querySelectorAll('[data-mobile-autoplay="true"]').forEach((slider) => {
    const mobile = window.matchMedia('(max-width: 980px)');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (reducedMotion.matches) return;
    let timer;
    let visible = false;
    let paused = false;
    const stop = () => {
      window.clearInterval(timer);
      timer = undefined;
    };
    const start = () => {
      stop();
      if (!visible || !mobile.matches) return;
      const speed = Number(slider.dataset.autoplaySpeed) || 4000;
      timer = window.setInterval(() => {
        if (paused || document.hidden) return;
        const card = slider.querySelector('.product-card');
        const gap = Number.parseFloat(window.getComputedStyle(slider).columnGap) || 0;
        const distance = (card?.getBoundingClientRect().width || slider.clientWidth) + gap;
        const maxScroll = slider.scrollWidth - slider.clientWidth;
        if (slider.scrollLeft >= maxScroll - 6) slider.scrollTo({ left: 0, behavior: 'smooth' });
        else slider.scrollBy({ left: distance, behavior: 'smooth' });
      }, speed);
    };
    const observer = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      if (visible) start(); else stop();
    }, { threshold: 0.35 });
    slider.addEventListener('pointerdown', () => { paused = true; });
    slider.addEventListener('pointerup', () => { paused = false; });
    slider.addEventListener('pointercancel', () => { paused = false; });
    mobile.addEventListener('change', start);
    observer.observe(slider);
  });

  const privacyBannerElement = document.querySelector('[data-cookie-banner]');
  if (privacyBannerElement) {
    let customerPrivacy;
    const showPrivacyBanner = () => {
      privacyBannerElement.hidden = false;
      privacyBannerElement.focus();
    };
    const hidePrivacyBanner = () => { privacyBannerElement.hidden = true; };
    const saveConsent = (consent) => {
      if (!customerPrivacy) return;
      customerPrivacy.setTrackingConsent(consent, hidePrivacyBanner);
    };

    document.querySelectorAll('[data-open-cookie-preferences]').forEach((button) => {
      button.addEventListener('click', () => {
        if (window.privacyBanner?.showPreferences) {
          window.privacyBanner.showPreferences().catch(showPrivacyBanner);
          return;
        }
        showPrivacyBanner();
      });
    });
    privacyBannerElement.querySelector('[data-cookie-essential]')?.addEventListener('click', () => {
      saveConsent({ analytics: false, marketing: false, preferences: false });
    });
    privacyBannerElement.querySelector('[data-cookie-accept]')?.addEventListener('click', () => {
      saveConsent({ analytics: true, marketing: true, preferences: true });
    });

    window.Shopify?.loadFeatures?.(
      [{ name: 'consent-tracking-api', version: '0.1' }],
      (error) => {
        if (error) return;
        customerPrivacy = window.Shopify.customerPrivacy;
        if (!window.privacyBanner && customerPrivacy?.shouldShowBanner()) showPrivacyBanner();
      },
    );
  }
});

document.addEventListener('shopify:section:load', (event) => {
  initHeroSlideshow(event.target);
  initTextureDashes(event.target);
  initFlashCountdown(event.target);
});
